import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { TreeNode as PrimeTreeNode } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ScrollerModule } from '@openng/optimus-ui/scroller';
import { TieredMenu } from '@openng/optimus-ui/tieredmenu';
import { Tree, TreeModule } from '@openng/optimus-ui/tree';

export interface TreeNode {
  id: string;
  name: string;
  icon?: string;
  routerLink?: string | unknown[];
  queryParams?: Params;
  children?: TreeNode[];
}

export interface TreeMenuOption {
  label: string;
  icon: string;
  command: (node: TreeNode) => Promise<void> | void;
}

@Component({
  selector: 'core-tree',
  imports: [
    CommonModule,
    RouterModule,
    Tree,
    ButtonModule,
    TieredMenu,
    TreeModule,
    ScrollerModule,
  ],
  template: `
    <p-tree
      [value]="primeNodes()"
      [virtualScroll]="true"
      [virtualScrollItemSize]="itemSize()"
      [scrollHeight]="scrollHeight()"
      (onNodeExpand)="onNodeExpand($event)"
      (onNodeCollapse)="onNodeCollapse($event)"
      [lazy]="true"
      data-testid="tree-component"
      class="w-full"
    >
      <ng-template let-node pTemplate="default">
        <div class="group flex items-center">
          <a
            [routerLink]="node.data.routerLink"
            [queryParams]="node.data.queryParams"
            class="cursor-pointer w-full"
          >
            <div class="flex items-center">
              <i
                [class]="
                  node.data.icon ? 'pi pi-' + node.data.icon : 'pi pi-folder'
                "
                data-testid="tree-node-icon"
                class="mr-2"
              ></i>
              <div
                data-testid="tree-node-label"
                [ngClass]="
                  selected() === node.data.id ? 'px-2 rounded-md bg-[red]' : ''
                "
              >
                {{ node.label }}
              </div>
            </div>
          </a>
          <button
            class="ml-auto bg-none text-white/80"
            [ngClass]="
              selected() === node.data.id ? 'block' : 'hidden group-hover:block'
            "
            data-testid="tree-menu-button"
            (click)="onMenuButtonClick($event, node.data)"
          >
            <i class="pi pi-ellipsis-v"></i>
          </button>
        </div>
      </ng-template>
    </p-tree>
    <p-tieredmenu [popup]="true" appendTo="body"></p-tieredmenu>
  `,
  styles: [
    `
      :host ::ng-deep .p-tree-node-label {
        width: 100% !important;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent {
  protected contextMenu = viewChild(TieredMenu);

  public readonly data = input<TreeNode[]>([]);
  public readonly scrollHeight = input<string>('600px');
  public readonly itemSize = input<number>(36);

  private readonly expandedNodeIds = signal<Set<string>>(new Set<string>());
  protected readonly primeNodes = computed(() =>
    toPrimeNodes(this.data(), this.expandedNodeIds()),
  );
  public readonly selected = model<string | undefined>(undefined);

  private readonly _expandSelectedNodePath = effect(() => {
    const currentData = this.data();
    if (currentData.length > 0) {
      setTimeout(() => this.expandPathToNode(this.selected()));
    }
  });

  private readonly _expandRootNodeByDefault = effect(() => {
    const currentData = this.data();
    if (currentData.length > 0 && currentData[0]) {
      this.expandedNodeIds.update(ids => {
        const newIds = new Set(ids);
        newIds.add(currentData[0].id);
        return newIds;
      });
    }
  });

  private expandPathToNode(nodeId: string | undefined): void {
    if (!nodeId) return;
    const path = getPath(this.data(), nodeId);
    if (!path) return;
    const nodeIdsToExpand = path.slice(0, -1).map(node => node.id);
    this.expandedNodeIds.update(ids => {
      const newIds = new Set(ids);
      nodeIdsToExpand.forEach(id => newIds.add(id));
      return newIds;
    });
  }

  public collapseAll(): void {
    this.expandedNodeIds.set(new Set<string>());
  }

  public expandNodeById(id: string): void {
    const path = getPath(this.data(), id);
    if (!path) return;
    const nodeIdsToExpand = path.map(node => node.id);
    this.expandedNodeIds.update(ids => {
      const newIds = new Set(ids);
      nodeIdsToExpand.forEach(id => newIds.add(id));
      return newIds;
    });
  }

  protected onNodeExpand(event: { node: PrimeTreeNode }): void {
    const nodeId = event.node.data.id;
    this.expandedNodeIds.update(ids => {
      const newIds = new Set(ids);
      newIds.add(nodeId);
      return newIds;
    });
  }

  protected onNodeCollapse(event: { node: PrimeTreeNode }): void {
    const nodeId = event.node.data.id;
    this.expandedNodeIds.update(ids => {
      const newIds = new Set(ids);
      newIds.delete(nodeId);
      return newIds;
    });
  }

  public readonly menuOptionsFn = input<(node: TreeNode) => TreeMenuOption[]>();

  protected onMenuButtonClick(event: MouseEvent, node: TreeNode): void {
    const menu = this.contextMenu();
    const getMenuOptions = this.menuOptionsFn();
    if (!menu || !getMenuOptions) return;

    menu.model = getMenuOptions(node).map(option => ({
      label: option.label,
      icon: `pi ${option.icon}`,
      command: () => option?.command(node),
    }));
    menu.toggle(event);

    event.stopPropagation();
  }
}

function toPrimeNodes(
  nodes: TreeNode[],
  expandedIds: Set<string>,
): PrimeTreeNode[] {
  if (!nodes || !Array.isArray(nodes)) {
    return [];
  }
  const transform = (node: TreeNode): PrimeTreeNode => {
    return {
      key: node.id,
      label: node.name,
      data: node,
      children: node.children?.map(transform) ?? [],
      leaf: node.children?.length === 0,
      expanded: expandedIds.has(node.id),
    } as PrimeTreeNode;
  };
  return nodes.map(transform);
}

export function getPath(nodes: TreeNode[], id: string): TreeNode[] | null {
  for (const node of nodes) {
    if (node.id === id) {
      return [node];
    }

    if (node.children?.length) {
      const childPath = getPath(node.children, id);
      if (childPath) {
        return [node, ...childPath];
      }
    }
  }
  return null;
}
